import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { useSiteText } from "@/context/SiteContentContext";

// -----------------------------------------------------------------------------
// MEMBER LOGIN — Bandcamp-style compact form
// -----------------------------------------------------------------------------
export function MemberLogin() {
    const { user, login, error } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const loc = useLocation();

    if (user && (user.role === "member" || user.role === "admin")) {
        const next = new URLSearchParams(loc.search).get("next") || "/member/statement";
        return <Navigate to={next} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const ok = await login(email.trim().toLowerCase(), password);
        setBusy(false);
        if (ok) navigate("/member/statement");
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="member-login-page">
            <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#5D3FD3]">member area</div>
                <h1 className="font-heading font-black text-4xl uppercase tracking-tighter leading-none">
                    sign&nbsp;in<span className="text-[#5D3FD3]">.</span>
                </h1>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
                    only for invited members of the collective.
                </p>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email"
                    required
                    data-testid="member-login-email"
                    className="w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-3"
                />
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="password"
                    required
                    data-testid="member-login-password"
                    className="w-full bg-transparent border border-[#333] focus:border-[#5D3FD3] outline-none font-mono text-sm p-3"
                />
                {error && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#B91C1C]" data-testid="member-login-error">
                        {formatApiError(error)}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="member-login-submit"
                    className="w-full bg-[#333] text-[#FFFFFF] hover:bg-[#5D3FD3] transition-colors py-3 font-mono text-xs uppercase tracking-[0.3em]"
                >
                    {busy ? "…" : "enter"}
                </button>
            </form>
        </div>
    );
}

// -----------------------------------------------------------------------------
// MEMBER LAYOUT — sidebar tabs
// -----------------------------------------------------------------------------
export function MemberLayout() {
    const { user, logout } = useAuth();
    const loc = useLocation();
    if (user === null) return <div className="p-8 font-mono text-xs">…</div>;
    if (!user || (user.role !== "member" && user.role !== "admin")) {
        return <Navigate to={`/member/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
    }
    const tabs = [
        { to: "/member/statement", label: "statement" },
        { to: "/member/finanzas", label: "finance" },
        { to: "/member/proyectos", label: "projects" },
    ];
    return (
        <div className="px-4 md:px-8 py-6 md:py-8" data-testid="member-layout">
            <div className="border-y border-[#333]/10 py-4 mb-8 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#555]">
                    member · <span className="text-[#333]">{user.name || user.email}</span>
                </div>
                <div className="flex flex-wrap gap-2 flex-1">
                    {tabs.map((tb) => (
                        <NavLink
                            key={tb.to}
                            to={tb.to}
                            data-testid={`member-tab-${tb.label}`}
                            className={({ isActive }) =>
                                `font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full transition-colors ${
                                    isActive
                                        ? "bg-[#333] text-[#FFFFFF]"
                                        : "bg-[#F5F5F5] text-[#333] hover:bg-[#333] hover:text-[#FFFFFF]"
                                }`
                            }
                        >
                            {tb.label}
                        </NavLink>
                    ))}
                </div>
                <button
                    onClick={logout}
                    data-testid="member-logout"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555] hover:text-[#B91C1C] transition-colors"
                >
                    sign out
                </button>
            </div>
            <Outlet />
        </div>
    );
}

const slugify = (str = "") =>
    String(str)
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const textOf = (children) => {
    if (typeof children === "string") return children;
    if (Array.isArray(children)) return children.map(textOf).join("");
    if (children && typeof children === "object" && children.props) return textOf(children.props.children);
    return "";
};

const MD_H1 = ({ children, ...props }) => <h1 id={slugify(textOf(children))} {...props}>{children}</h1>;
const MD_H2 = ({ children, ...props }) => <h2 id={slugify(textOf(children))} {...props}>{children}</h2>;
const MD_H3 = ({ children, ...props }) => <h3 id={slugify(textOf(children))} {...props}>{children}</h3>;
const MD_COMPONENTS = { h1: MD_H1, h2: MD_H2, h3: MD_H3 };

// -----------------------------------------------------------------------------
// INVITE-TO-JOIN FORM — shown at the bottom of the statement.
// Prospective members (people who received the invite link + guest credentials)
// read the statement, then can drop their email if they want to apply.
// -----------------------------------------------------------------------------
function StatementInviteForm({ lang }) {
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [status, setStatus] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setStatus("sending");
        try {
            await api.post("/interest", {
                email: email.trim(),
                note: note.trim(),
                source: "statement_invite",
            });
            setStatus("ok");
            setEmail("");
            setNote("");
        } catch {
            setStatus("err");
        }
    };
    return (
        <div className="mt-16 border-t border-[#333]/15 pt-10" data-testid="statement-invite">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#5D3FD3] mb-3">
                {lang === "es" ? "invitación" : "invitation"}
            </div>
            <h3 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight leading-tight text-[#333] mb-3">
                {lang === "es" ? "¿te interesa?" : "interested?"}
            </h3>
            <p className="font-body text-sm md:text-base text-[#555] leading-relaxed max-w-xl mb-5">
                {lang === "es"
                    ? "deja tu correo y cuéntanos un poco sobre ti y lo que haces."
                    : "leave your email and tell us a bit about you and what you do."}
            </p>
            <form onSubmit={submit} className="flex flex-col md:flex-row gap-2 max-w-2xl">
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={lang === "es" ? "tu correo" : "your email"}
                    data-testid="statement-invite-email"
                    className="flex-1 bg-transparent border border-[#333] font-mono text-sm p-3 focus:border-[#5D3FD3] outline-none"
                />
                <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={lang === "es" ? "qué te apasiona (opcional)" : "what you're into (optional)"}
                    data-testid="statement-invite-note"
                    className="flex-1 bg-transparent border border-[#333] font-mono text-sm p-3 focus:border-[#5D3FD3] outline-none"
                />
                <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="statement-invite-submit"
                    className="bg-[#333] text-[#FFFFFF] hover:bg-[#5D3FD3] transition-colors px-6 py-3 font-mono text-[10px] uppercase tracking-[0.3em]"
                >
                    {status === "sending" ? "…" : (lang === "es" ? "quiero participar" : "count me in")}
                </button>
            </form>
            {status === "ok" && (
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5D3FD3]" data-testid="statement-invite-ok">
                    {lang === "es" ? "recibido. gracias — te contactamos pronto." : "received. thanks — we'll be in touch."}
                </div>
            )}
            {status === "err" && (
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#B91C1C]" data-testid="statement-invite-err">
                    {lang === "es" ? "error — intenta de nuevo." : "error — try again."}
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// STATEMENT — editorial-style layout with sticky TOC + anchored sections
// -----------------------------------------------------------------------------
export function MemberStatement() {
    const { t, lang } = useT();
    const st = useSiteText(lang, t);
    const md = st("member_statement");

    // Parse H2s for the TOC
    const toc = (md || "")
        .split("\n")
        .filter((l) => l.startsWith("## ") && !l.startsWith("### "))
        .map((l) => l.replace(/^##\s+/, "").trim())
        .map((title) => ({ title, id: slugify(title) }));

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div data-testid="member-statement" className="grid grid-cols-12 gap-8 md:gap-12">
            {/* Sticky TOC — desktop only */}
            {toc.length > 0 && (
                <aside className="hidden md:block col-span-3">
                    <div className="sticky top-24">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-4">contents</div>
                        <ol className="space-y-2">
                            {toc.map((item, i) => (
                                <li key={item.id}>
                                    <button
                                        onClick={() => scrollTo(item.id)}
                                        data-testid={`member-toc-${item.id}`}
                                        className="text-left font-mono text-[11px] uppercase tracking-[0.15em] text-[#555] hover:text-[#5D3FD3] transition-colors"
                                    >
                                        <span className="text-[#999] mr-2">{String(i + 1).padStart(2, "0")}</span>
                                        {item.title}
                                    </button>
                                </li>
                            ))}
                        </ol>
                        <div className="mt-6 pt-6 border-t border-[#DDD]">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#999] mb-2">splits at a glance</div>
                            <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="border border-[#DDD] py-3">
                                    <div className="font-heading font-black text-2xl text-[#333]">60%</div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">collab</div>
                                </div>
                                <div className="border border-[#DDD] py-3">
                                    <div className="font-heading font-black text-2xl text-[#5D3FD3]">40%</div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#555]">collective</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            )}

            {/* Body */}
            <div className={`col-span-12 ${toc.length ? "md:col-span-9" : ""}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3] mb-3">statement</div>
                {toc.length > 0 && (
                    <details className="md:hidden mb-6 border-b border-[#DDD] pb-4" data-testid="member-toc-mobile">
                        <summary className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#555] cursor-pointer">contents ({toc.length})</summary>
                        <ol className="mt-3 space-y-1.5">
                            {toc.map((item, i) => (
                                <li key={item.id}>
                                    <button onClick={() => scrollTo(item.id)} className="text-left font-mono text-[11px] uppercase tracking-[0.15em] text-[#555]">
                                        <span className="text-[#999] mr-2">{String(i + 1).padStart(2, "0")}</span>{item.title}
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </details>
                )}
                <div className="member-md">
                    {md ? (
                        <ReactMarkdown components={MD_COMPONENTS}>{md}</ReactMarkdown>
                    ) : (
                        <div className="font-mono text-xs text-[#555]">no statement yet.</div>
                    )}
                </div>
                <StatementInviteForm lang={lang} />
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// FINANCE
// -----------------------------------------------------------------------------
export function MemberFinanzas() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/member/finance")
            .then((r) => setData(r.data))
            .catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "error"));
    }, []);
    if (err) return <div className="font-mono text-xs text-[#B91C1C]" data-testid="member-finance-error">{err}</div>;
    if (!data) return <div className="font-mono text-xs">…</div>;
    const tot = data.totals || {};
    const rows = data.rows || [];
    const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;
    return (
        <div data-testid="member-finance" className="space-y-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">finance</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    ["lifetime gross", fmt(tot.lifetime_gross), "member-total-gross"],
                    ["paid out", fmt(tot.lifetime_payouts), "member-total-paid"],
                    ["outstanding", fmt(tot.outstanding), "member-total-outstanding"],
                    [`next payout · ${tot.next_payout_date || "—"}`, fmt(tot.next_payout_estimate), "member-total-next"],
                ].map(([k, v, tid]) => (
                    <div key={k} className="border border-[#333]/15 p-4" data-testid={tid}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555]">{k}</div>
                        <div className="mt-2 font-heading font-black text-2xl">{v}</div>
                    </div>
                ))}
            </div>
            <div className="border-y border-[#333]/10">
                <div className="grid grid-cols-12 gap-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">
                    <div className="col-span-3">date</div>
                    <div className="col-span-5">project</div>
                    <div className="col-span-2">type</div>
                    <div className="col-span-1 text-right">gross</div>
                    <div className="col-span-1 text-right">status</div>
                </div>
                {rows.map((r, i) => (
                    <div key={i} data-testid={`member-finance-row-${i}`} className="grid grid-cols-12 gap-3 py-3 border-t border-[#333]/10 font-mono text-xs">
                        <div className="col-span-3 text-[#555]">{r.date}</div>
                        <div className="col-span-5 text-[#333]">{r.project}</div>
                        <div className="col-span-2 text-[#555] uppercase">{r.type}</div>
                        <div className="col-span-1 text-right">{fmt(r.gross)}</div>
                        <div className={`col-span-1 text-right uppercase ${r.status === "paid" ? "text-[#333]" : "text-[#5D3FD3]"}`}>{r.status}</div>
                    </div>
                ))}
                {rows.length === 0 && (
                    <div className="py-6 font-mono text-xs text-[#555] uppercase tracking-[0.2em]">no payouts yet.</div>
                )}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// PROJECTS
// -----------------------------------------------------------------------------
export function MemberProyectos() {
    const [projects, setProjects] = useState([]);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/member/projects")
            .then((r) => setProjects(r.data))
            .catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "error"));
    }, []);
    if (err) return <div className="font-mono text-xs text-[#B91C1C]" data-testid="member-projects-error">{err}</div>;
    const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;
    return (
        <div data-testid="member-projects" className="space-y-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5D3FD3]">projects</div>
            {projects.length === 0 && (
                <div className="font-mono text-xs text-[#555] uppercase tracking-[0.2em]">no projects yet.</div>
            )}
            {projects.map((p) => (
                <details key={p.id} data-testid={`member-project-${p.id}`} className="border-t border-[#333]/10 py-4">
                    <summary className="cursor-pointer flex flex-wrap items-baseline gap-4 group">
                        <span className="font-heading font-black text-lg md:text-xl uppercase tracking-tight">{p.title}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
                            {p.period_start} → {p.period_end}
                        </span>
                        <span className="ml-auto font-mono text-sm">{fmt(p.net_profit)} <span className="text-[#555] text-[10px] uppercase">net</span></span>
                    </summary>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 font-mono text-xs">
                        {[
                            ["sales", p.sales],
                            ["costs", p.costs],
                            ["net", p.net_profit],
                            ["collab 60%", p.collab_share],
                            ["collective 40%", p.collective_share],
                        ].map(([k, v]) => (
                            <div key={k} className="border border-[#333]/10 p-3">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#555]">{k}</div>
                                <div className="mt-1 text-sm">{fmt(v)}</div>
                            </div>
                        ))}
                    </div>
                    {p.collaborators?.length > 0 && (
                        <div className="mt-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#555] mb-2">collaborators</div>
                            <div className="divide-y divide-[#333]/10">
                                {p.collaborators.map((c, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-3 py-2 font-mono text-xs">
                                        <div className="col-span-4">{c.name}</div>
                                        <div className="col-span-5 text-[#555]">{c.role}</div>
                                        <div className="col-span-2 uppercase text-[#5D3FD3]">{c.type}</div>
                                        <div className="col-span-1 text-right">{fmt(c.amount)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </details>
            ))}
        </div>
    );
}
